import type { KanbanComment } from '@entities/kanban-comment'

import { useKanbanCommentsStore } from '@entities/kanban-comment'
import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface DisciplineTaskCommentsApiOptions {
  customerId: string | number
  disciplineId?: (string | number)[]
  createdFrom?: string | null
  createdTo?: string | null
  onStart?: () => void
  onSuccess?: (data: KanbanComment[]) => void
  onError?: (err: unknown) => void
  onFinish?: () => void
}

const buildApiParams = (options: DisciplineTaskCommentsApiOptions): Record<string, string | number | (string | number)[]> => {
  const apiParams: Record<string, string | number | (string | number)[]> = {
    customer_id: options.customerId,
  }

  if (options.disciplineId?.length) apiParams.discipline_id = options.disciplineId
  if (options.createdFrom) apiParams.created_from = options.createdFrom
  if (options.createdTo) apiParams.created_to = options.createdTo

  return apiParams
}

export const useDisciplineTaskCommentsApi = async (options: DisciplineTaskCommentsApiOptions): Promise<KanbanComment[]> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.kanban-tasks-comments', buildApiParams(options))

  const kanbanCommentsStore = useKanbanCommentsStore()
  const commonStore = useCommonStore()

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    const { data } = await $axios.get(url!)
    const comments: KanbanComment[] = data.data

    kanbanCommentsStore.setComments(comments)
    kanbanCommentsStore.setCustomerId(options.customerId)
    commonStore.setLoading(false)

    options.onSuccess?.(comments)

    return comments
  } catch (err: unknown) {
    console.error(err)
    toast.error(getAxiosErrorMessage(err))
    commonStore.setLoading(false)
    options.onError?.(err)

    return []
  } finally {
    options.onFinish?.()
  }
}